import { PersonSelect } from '../ui/PersonSelect';
import { TaskTypeDot } from './TaskTypeDot';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import type { Task, TaskSection } from '../../hooks/queries/useTasks';
import {
  useTasksAssignments,
  useCommentCounts,
  useAttachmentCounts,
} from '../../hooks/queries/useTaskCollaboration';
import { useTaskTagsFor } from '../../hooks/queries/useTaskTags';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useUndoableTaskDelete } from '../../hooks/useUndoableTaskDelete';
import { useTasksStore, type GroupMode } from '../../store/tasksStore';
import { rangeSelection } from '../../lib/tasks/taskSelection';
import { useTaskCompletion } from '../../hooks/useTaskCompletion';
import { TaskDoneCircle, TaskSelectBox } from './TaskRowControls';
import { cn } from '../../lib/cn';
import { readableTextColor } from '../../lib/colorContrast';
import { deadlineUrgency } from '../../services/dossiers/dossierListDerive';
import {
  matchesTaskTableFilters,
  sortTaskRows,
  type TaskSortDir,
  type TaskTableColumn,
  type TaskTableFilters,
  type TaskTableRow,
} from '../../services/tasks/taskTableDerive';
import type { TaskDossierRef } from './TaskCard';
import { TaskComposer } from './TaskComposer';
import { TaskPreviewCard } from './TaskPreviewCard';
import { useTaskHoverPreview } from '../../hooks/queries/useCabinetSettings';
import { useTaskTagDisplay } from '../../hooks/useTaskTagDisplay';
import { TaskTagStripe } from './TaskTagStripe';
import { AssigneeLabel } from './AssigneeLabel';
import { getDueDate, getPriorityMeta } from './taskHelpers';
import { buildTaskPreview, priorityTone } from './taskPreview';
import { extractDueTime } from '../../lib/tasks/taskReschedule';
import { useTaskListPreview } from './useTaskListPreview';
import { TaskContextMenu, useTaskContextMenu } from './TaskContextMenu';

import { formatAppDate } from '../../lib/dateFormat';
import { ResizableColumns } from '../ui/ResizableColumns';
interface TasksListViewProps {
  tasks: Task[];
  sections: TaskSection[];
  dossierId: string | null;
  onOpenTask?: (task: Task) => void;
  /** Résolution dossier_id → réf pour afficher le dossier sur chaque tâche. */
  dossierLookup?: (id: string) => TaskDossierRef | null;
  /** Affiche le dossier rattaché (vue « tous les dossiers »). */
  showDossier?: boolean;
  /**
   * Conservé pour les appelants. Le tableau ne groupe plus par section :
   * le tri se fait sur les en-têtes.
   */
  lockedGroupMode?: GroupMode;
  /** Masque le composer inline (création via modal ailleurs). */
  hideComposer?: boolean;
  /** Conservé pour les appelants. Le tri du tableau est sur les en-têtes. */
  hideSort?: boolean;
}

const COLUMN_LABEL: Record<TaskTableColumn, string> = {
  due: 'Échéance',
  subject: 'Sujet',
  owner: 'Responsable',
  status: 'Statut',
  dossier: 'Dossier',
};

const STATUS_OPTIONS = [
  { value: 'open', label: 'À faire' },
  { value: 'done', label: 'Terminée' },
];

const ECHEANCE_OPTIONS = [
  { value: 'overdue', label: 'En retard' },
  { value: 'week', label: 'Dans les 7 jours' },
  { value: 'none', label: 'Sans date' },
];

const TASK_COLUMNS_KEY = 'cime.tasks.columns';

const OPTIONAL_TASK_COLUMNS = [
  { key: 'due', label: 'Échéance' },
  { key: 'dossier', label: 'Dossier' },
  { key: 'owner', label: 'Responsable' },
  { key: 'status', label: 'Statut' },
] as const;

type OptionalTaskColumn = (typeof OPTIONAL_TASK_COLUMNS)[number]['key'];
type TaskColumnFlags = Record<OptionalTaskColumn, boolean>;

function defaultTaskColumns(): TaskColumnFlags {
  return { due: true, dossier: true, owner: true, status: true };
}

function loadTaskColumns(): TaskColumnFlags {
  const flags = defaultTaskColumns();
  try {
    const raw = localStorage.getItem(TASK_COLUMNS_KEY);
    if (!raw) return flags;
    const parsed = JSON.parse(raw) as Partial<TaskColumnFlags>;
    for (const col of OPTIONAL_TASK_COLUMNS) {
      const value = parsed[col.key];
      if (typeof value === 'boolean') flags[col.key] = value;
    }
  } catch {
    return defaultTaskColumns();
  }
  return flags;
}

const OPEN_FILTERS: TaskTableFilters = {
  status: 'open',
  ownerId: null,
  echeance: null,
  dossierId: null,
};

const URGENCY_TEXT: Record<string, string> = {
  overdue: 'text-[#c05b33]',
  urgent: 'text-[#c05b33]',
  soon: 'text-cime-gold',
  normal: 'text-cime-text-primary',
};

function dueLabel(iso: string | null, time: string | null): string {
  if (!iso) return '';
  const day = iso.slice(0, 10);
  const [year, month, date] = day.split('-').map(Number);
  if (!year || !month || !date) return '';
  const text = formatAppDate(new Date(year, month - 1, date));
  return time ? `${text} · ${time}` : text;
}

function toRow(
  task: Task,
  ownerId: string | null,
  owner: string,
  dossier: string,
): TaskTableRow {
  return {
    id: task.id,
    subject: task.content,
    due: getDueDate(task),
    ownerId,
    owner,
    done: task.is_completed,
    dossierId: task.dossier_id,
    dossier,
  };
}

export function TasksListView({
  tasks,
  sections,
  dossierId,
  onOpenTask,
  dossierLookup,
  showDossier = false,
  hideComposer = false,
}: TasksListViewProps) {
  const { tags: tagsEnabled, priority: priorityEnabled, sections: sectionsEnabled } = useEffectiveTaskFields();
  const pendingDeleteIds = useTasksStore((s) => s.pendingDeleteIds);
  const completion = useTaskCompletion();
  const selectedTaskIds = useTasksStore((s) => s.selectedTaskIds);
  const selectionAnchorId = useTasksStore((s) => s.selectionAnchorId);
  const toggleSelected = useTasksStore((s) => s.toggleSelected);
  const selectMany = useTasksStore((s) => s.selectMany);
  const clearSelection = useTasksStore((s) => s.clearSelection);
  const requestDelete = useUndoableTaskDelete();

  const [filters, setFilters] = useState<TaskTableFilters>(OPEN_FILTERS);
  const [sortColumn, setSortColumn] = useState<TaskTableColumn>('due');
  const [sortDir, setSortDir] = useState<TaskSortDir>('asc');
  const [columnFlags, setColumnFlags] = useState<TaskColumnFlags>(loadTaskColumns);

  function saveColumns(next: TaskColumnFlags) {
    setColumnFlags(next);
    localStorage.setItem(TASK_COLUMNS_KEY, JSON.stringify(next));
  }

  const visibleTasks = useMemo(
    () => tasks.filter((t) => !pendingDeleteIds.has(t.id)),
    [tasks, pendingDeleteIds],
  );
  const taskIds = useMemo(() => visibleTasks.map((t) => t.id), [visibleTasks]);
  const { data: assignmentsMap } = useTasksAssignments(taskIds);
  const { data: commentCounts } = useCommentCounts(taskIds);
  const { data: attachmentCounts } = useAttachmentCounts(taskIds);
  const { data: tagMap = {} } = useTaskTagsFor(tagsEnabled ? taskIds : []);

  const appliedFilters = useMemo<TaskTableFilters>(
    () => ({ ...filters, dossierId: showDossier ? filters.dossierId : null }),
    [filters, showDossier],
  );

  const rows = useMemo(() => {
    return visibleTasks.map((task) => {
      const people = assignmentsMap?.get(task.id) ?? [];
      const first = people[0];
      const dossier = task.dossier_id ? dossierLookup?.(task.dossier_id) : null;
      return toRow(
        task,
        first?.id ?? null,
        first?.full_name?.trim() || first?.email || '',
        dossier?.title ?? '',
      );
    });
  }, [visibleTasks, assignmentsMap, dossierLookup]);

  const shownRows = useMemo(
    () => sortTaskRows(
      rows.filter((row) => matchesTaskTableFilters(row, appliedFilters)),
      sortColumn,
      sortDir,
    ),
    [rows, appliedFilters, sortColumn, sortDir],
  );
  const taskById = useMemo(
    () => new Map(visibleTasks.map((task) => [task.id, task])),
    [visibleTasks],
  );
  const orderedIds = useMemo(() => shownRows.map((row) => row.id), [shownRows]);

  useEffect(() => {
    clearSelection();
  }, [appliedFilters, clearSelection]);

  const dossierOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const task of visibleTasks) {
      if (!task.dossier_id || seen.has(task.dossier_id)) continue;
      const ref = dossierLookup?.(task.dossier_id);
      seen.set(task.dossier_id, ref?.title || 'Dossier');
    }
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }, [visibleTasks, dossierLookup]);

  function selectSort(column: TaskTableColumn) {
    if (sortColumn === column) setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(column);
      setSortDir('asc');
    }
  }

  function handleToggleSelect(id: string, shiftKey: boolean) {
    if (shiftKey) {
      const ids = rangeSelection(orderedIds, selectionAnchorId, id);
      if (ids.length > 0) selectMany(ids, id);
    } else {
      toggleSelected(id);
    }
  }

  const menu = useTaskContextMenu();
  const hoverPreview = useTaskHoverPreview();
  const tagDisplay = useTaskTagDisplay();
  const preview = useTaskListPreview(!hoverPreview || menu.target !== null);
  const hovered = preview.hoverId ? taskById.get(preview.hoverId) ?? null : null;
  const previewModel = hovered
    ? buildTaskPreview({
        title: hovered.content,
        description: hovered.description,
        isCompleted: hovered.is_completed,
        dueIso: getDueDate(hovered),
        dueTime: extractDueTime(hovered.due_datetime),
        deadlineIso: hovered.deadline,
        dossierTitle:
          showDossier && hovered.dossier_id
            ? dossierLookup?.(hovered.dossier_id)?.title ?? null
            : null,
        priority: priorityEnabled
          ? {
              label: getPriorityMeta(hovered.priority ?? 3).label,
              tone: priorityTone(hovered.priority ?? 3),
            }
          : null,
        sectionName: sectionsEnabled
          ? sections.find((s) => s.id === hovered.section_id)?.name ?? null
          : null,
        tags: tagsEnabled ? (tagMap[hovered.id] ?? []) : [],
        people: (assignmentsMap?.get(hovered.id) ?? []).map((person) => ({
          id: person.id,
          name: person.full_name?.trim() || person.email || 'Intervenant',
          avatarUrl: person.avatar_url,
        })),
        commentCount: commentCounts?.get(hovered.id) ?? 0,
        attachmentCount: attachmentCounts?.get(hovered.id) ?? 0,
      }, new Date())
    : null;

  const filtersActive =
    appliedFilters.status !== null ||
    appliedFilters.ownerId !== null ||
    appliedFilters.echeance !== null ||
    appliedFilters.dossierId !== null;

  const columns: TaskTableColumn[] = (['subject', 'dossier', 'owner', 'status', 'due'] as const).filter((column) => {
    if (column === 'subject') return true;
    if (column === 'dossier') return showDossier && columnFlags.dossier;
    return columnFlags[column];
  });

  return (
    <div className="space-y-3 w-full mx-auto">
      <div className="flex flex-col gap-2">
          {!hideComposer && (
            <TaskComposer
              dossierId={dossierId}
              placeholder="Nouvelle tâche — ajouter à la liste"
            />
          )}
          <div className="flex w-full flex-wrap items-center gap-2">
            <FilterPill
              mode="single"
              label="Statut"
              options={STATUS_OPTIONS}
              selected={appliedFilters.status ? [appliedFilters.status] : []}
              onChange={(values) => {
                const status = values[0];
                setFilters((current) => ({
                  ...current,
                  status: status === 'open' || status === 'done' ? status : null,
                }));
              }}
            />
            <PersonSelect
              single
              prefix="Responsable"
              emptyLabel="Tous"
              value={appliedFilters.ownerId ? [appliedFilters.ownerId] : []}
              onChange={(values) => setFilters((current) => ({ ...current, ownerId: values[0] ?? null }))}
            />
            <FilterPill
              mode="single"
              label="Échéance"
              options={ECHEANCE_OPTIONS}
              selected={appliedFilters.echeance ? [appliedFilters.echeance] : []}
              onChange={(values) => {
                const echeance = values[0];
                setFilters((current) => ({
                  ...current,
                  echeance: echeance === 'overdue' || echeance === 'week' || echeance === 'none' ? echeance : null,
                }));
              }}
            />
            {showDossier && (
              <FilterPill
                mode="single"
                label="Dossier"
                options={dossierOptions}
                selected={appliedFilters.dossierId ? [appliedFilters.dossierId] : []}
                onChange={(values) => setFilters((current) => ({ ...current, dossierId: values[0] ?? null }))}
              />
            )}
            {filtersActive && (
              <button
                type="button"
                onClick={() => setFilters({ status: null, ownerId: null, echeance: null, dossierId: null })}
                className="text-xs text-[#8a928c] underline underline-offset-2 hover:text-[#1f2823]"
              >
                Réinitialiser
              </button>
            )}
            <ColumnMenu flags={columnFlags} showDossier={showDossier} onChange={saveColumns} />
          </div>
        </div>

      {shownRows.length === 0 ? (
        <div className="text-sm text-cime-text-muted italic px-4 py-8 border border-dashed border-cime-border rounded-xl text-center">
          Aucune tâche ne correspond.
        </div>
      ) : (
        <div className="w-full min-w-0 overflow-hidden rounded-xl border border-cime-border bg-white">
          <div className="w-full min-w-0 overflow-x-auto">
            <table className="cime-table" style={{ width: 'max-content', minWidth: '100%' }}>
              <ResizableColumns id="tasks" />
              <thead>
                <tr>
                  <th className="w-10 px-3 py-2.5" aria-hidden />
                  {columns.map((column) => (
                    <SortHeader
                      key={column}
                      column={column}
                      sortColumn={sortColumn}
                      sortDir={sortDir}
                      onSort={selectSort}
                    />
                  ))}
                  <th className="w-16 px-2 py-2.5" aria-hidden />
                </tr>
              </thead>
              <tbody>
                {shownRows.map((row) => {
                  const task = taskById.get(row.id);
                  if (!task) return null;
                  const people = assignmentsMap?.get(task.id) ?? [];
                  const dossier = task.dossier_id ? dossierLookup?.(task.dossier_id) ?? null : null;
                  const tags = tagsEnabled ? (tagMap[task.id] ?? []) : [];
                  const due = getDueDate(task);
                  const urgency = due ? deadlineUrgency(due) : null;
                  const selected = selectedTaskIds.has(task.id);
                  return (
                    <tr
                      key={task.id}
                      className={cn('group h-11 cursor-pointer', selected && 'bg-cime-primary/5')}
                      onClick={(event) => {
                        if (!onOpenTask) return;
                        const target = event.target as HTMLElement;
                        if (target.closest('button, a, [role="checkbox"]')) return;
                        onOpenTask(task);
                      }}
                      onMouseEnter={(event) => preview.onEnter(task.id, event.currentTarget)}
                      onMouseLeave={preview.onLeave}
                      onContextMenu={(event) => menu.open(event, task)}
                    >
                      <td className="relative h-11 px-3 align-middle">
                        {tagDisplay === 'border' ? <TaskTagStripe tags={tags} /> : null}
                        <TaskDoneCircle done={!!task.is_completed} onToggle={() => completion.toggle(task)} />
                      </td>
                      <td className="h-11 max-w-[420px] px-3 align-middle">
                        <div className="flex h-11 min-w-0 items-center gap-2 overflow-hidden">
                          <span
                            className={cn(
                              'min-w-0 truncate text-sm',
                              task.is_completed ? 'text-cime-text-muted' : 'text-cime-text-primary',
                            )}
                          >
                            <TaskTypeDot templateId={task.task_template_id} />
                            {task.content}
                          </span>
                          {tagDisplay === 'chips' && tags.length > 0 && (
                            <span className="flex min-w-0 max-w-[46%] shrink items-center gap-1 overflow-hidden whitespace-nowrap">
                              {tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="shrink-0 truncate rounded-full px-1.5 text-[10px] leading-4"
                                  style={{ backgroundColor: tag.color, color: readableTextColor(tag.color) }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                      </td>
                      {showDossier && columnFlags.dossier && (
                        <td className="h-11 max-w-[220px] px-3 align-middle">
                          {dossier ? (
                            <Link
                              to={`/dossiers/${dossier.id}`}
                              onClick={(event) => event.stopPropagation()}
                              className="group/dossier block min-w-0 leading-tight"
                              title={dossier.number ? `${dossier.title} — ${dossier.number}` : dossier.title}
                            >
                              {dossier.number ? (
                                <span className="block truncate text-[11px] tracking-wide text-neutral-400">
                                  {dossier.number}
                                </span>
                              ) : null}
                              <span className="block truncate text-[13px] text-cime-text-primary group-hover/dossier:text-cime-primary group-hover/dossier:underline">
                                {dossier.title}
                              </span>
                            </Link>
                          ) : (
                            <span className="text-neutral-300">—</span>
                          )}
                        </td>
                      )}
                      {columnFlags.owner && (
                        <td className="h-11 max-w-[180px] px-3 align-middle">
                          <div className="flex h-11 items-center overflow-hidden">
                            {people.length > 0 ? (
                              <AssigneeLabel assignees={people} size="xs" />
                            ) : (
                              <span className="text-neutral-300">—</span>
                            )}
                          </div>
                        </td>
                      )}
                      {columnFlags.status && (
                        <td className="h-11 whitespace-nowrap px-3 align-middle text-sm text-cime-text-primary">
                          {task.is_completed ? 'Terminée' : 'À faire'}
                        </td>
                      )}
                      {columnFlags.due && (
                        <td className="h-11 whitespace-nowrap px-3 align-middle text-sm">
                          {due ? (
                            <span className={cn('font-medium', urgency ? URGENCY_TEXT[urgency] : undefined)}>
                              {dueLabel(due, extractDueTime(task.due_datetime))}
                            </span>
                          ) : (
                            <span className="text-neutral-300">—</span>
                          )}
                        </td>
                      )}
                      <td className="h-11 px-2 align-middle">
                        <div className="flex items-center justify-end gap-2.5">
                          <TaskSelectBox
                            selected={selected}
                            selectionActive={selectedTaskIds.size > 0}
                            onToggle={(shiftKey) => handleToggleSelect(task.id, shiftKey)}
                          />
                          <button
                            type="button"
                            onClick={() => requestDelete({ id: task.id, content: task.content })}
                            className="text-neutral-400 opacity-0 transition hover:text-red-600 group-hover:opacity-100"
                            aria-label="Supprimer la tâche"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="border-t border-neutral-200 bg-neutral-50 px-4 py-2 text-xs text-neutral-400">
            {shownRows.length} tâche{shownRows.length > 1 ? 's' : ''}
            {' · '}
            tri {COLUMN_LABEL[sortColumn].toLocaleLowerCase('fr')}, {sortDir === 'asc' ? 'croissant' : 'décroissant'}
          </div>
        </div>
      )}

      {previewModel && preview.rect && createPortal(
        <TaskPreviewCard
          key={preview.hoverId}
          model={previewModel}
          anchor={preview.rect}
          shown={preview.shown}
        />,
        document.body,
      )}
      {menu.target && onOpenTask ? (
        <TaskContextMenu target={menu.target} onClose={menu.close} onOpenTask={onOpenTask} />
      ) : null}
    </div>
  );
}

function SortHeader({
  column, sortColumn, sortDir, onSort,
}: {
  column: TaskTableColumn;
  sortColumn: TaskTableColumn;
  sortDir: TaskSortDir;
  onSort: (column: TaskTableColumn) => void;
}) {
  const on = sortColumn === column;
  return (
    <th
      aria-sort={on ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="whitespace-nowrap px-3 py-2.5 text-left"
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn('inline-flex items-center gap-1 uppercase tracking-inherit', on && 'text-cime-gold')}
      >
        {COLUMN_LABEL[column]}
        <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
          <path d={on && sortDir === 'desc' ? 'M6 10L2 5h8z' : 'M6 2l4 5H2z'} fill="currentColor" />
        </svg>
      </button>
    </th>
  );
}

function ColumnMenu({
  flags, showDossier, onChange,
}: {
  flags: TaskColumnFlags;
  showDossier: boolean;
  onChange: (next: TaskColumnFlags) => void;
}) {
  const [open, setOpen] = useState(false);
  const options = OPTIONAL_TASK_COLUMNS.filter((col) => col.key !== 'dossier' || showDossier);
  const count = options.filter((col) => flags[col.key]).length;

  return (
    <div className="relative ml-auto">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'inline-flex h-8 items-center gap-2 rounded-full border bg-white py-0 pl-3 pr-2.5 text-[13px] font-bold text-[#1f2823]',
          open
            ? 'border-[#043429] shadow-[inset_0_0_0_1px_#043429]'
            : 'border-[#e6e2d8] hover:border-[#cfcab8]',
        )}
      >
        <svg className="text-[#043429]" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden>
          <rect x="1.5" y="2" width="13" height="12" rx="1.5" />
          <path d="M1.5 6h13M6.5 6v8M11 6v8" />
        </svg>
        Colonnes
        <span className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[#043429] px-[5px] text-[11px] font-bold text-white">{count}</span>
        <MenuChevron open={open} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className={cn(MENU_PANEL, 'right-0 w-[280px]')}>
            <p className="mx-2.5 mb-1 mt-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a928c]">Affichage</p>
            {options.map((col) => {
              const checked = flags[col.key];
              return (
                <label
                  key={col.key}
                  className={cn(
                    'my-px flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-sm text-[#1f2823] hover:bg-[#f4f1e8]',
                    checked && 'bg-[rgba(201,168,76,0.16)] font-bold text-[#043429]',
                  )}
                >
                  <span>{col.label}</span>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={checked}
                    onChange={() => onChange({ ...flags, [col.key]: !checked })}
                  />
                  <MenuMark checked={checked} />
                </label>
              );
            })}
            <p className="mx-2.5 mb-1 mt-2 border-t border-[#e6e2d8] pt-2 text-xs leading-snug text-[#8a928c]">
              Le sujet reste affiché.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

const MENU_PANEL =
  'absolute top-[38px] z-20 max-h-[min(480px,70vh)] overflow-auto rounded-[14px] border border-[#e6e2d8] bg-[#fffdf8] p-2 shadow-[0_16px_40px_rgba(4,52,41,0.12)]';

function FilterPill({
  label, options, selected, onChange, mode = 'single',
}: {
  label: string;
  options: readonly { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  mode?: 'single';
}) {
  const [open, setOpen] = useState(false);
  const active = selected.length > 0;
  const summary = active
    ? `${label} · ${options.find((option) => option.value === selected[0])?.label ?? label}`
    : label;

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'inline-flex h-8 max-w-[250px] items-center gap-[7px] rounded-full border bg-white py-0 pl-3 pr-2.5 text-[13px]',
          open
            ? 'border-solid border-[#043429] text-[#043429]'
            : active
              ? 'border-solid border-[#e0d3a4] bg-cime-gold-bg font-bold text-cime-gold-text'
              : 'border-dashed border-[#d4cfc3] text-[#5c6660] hover:border-[#cfcab8] hover:text-[#1f2823]',
        )}
      >
        <span className="truncate">{summary}</span>
        <MenuChevron open={open} active={active} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className={cn(MENU_PANEL, 'left-0 w-[272px]')}>
            <p className="mx-2.5 mb-1 mt-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-[#8a928c]">{label}</p>
            {options.length === 0 && (
              <p className="px-2.5 py-2 text-xs text-[#8a928c]">Aucune option</p>
            )}
            {options.map((option) => {
              const checked = selected.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={cn(
                    'my-px flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-sm text-[#1f2823]',
                    checked ? 'bg-[rgba(201,168,76,0.16)] font-bold text-[#043429]' : 'hover:bg-[#f4f1e8]',
                  )}
                >
                  <span className="truncate">{option.label}</span>
                  <input
                    type="radio"
                    name={label}
                    checked={checked}
                    onClick={(event) => {
                      if (!checked) return;
                      event.preventDefault();
                      onChange([]);
                      setOpen(false);
                    }}
                    onChange={() => {
                      onChange(checked ? [] : [option.value]);
                      if (mode === 'single') setOpen(false);
                    }}
                    className="sr-only"
                  />
                  <MenuMark checked={checked} />
                </label>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function MenuChevron({ open, active }: { open: boolean; active?: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      aria-hidden
      className={cn(
        'h-3 w-3 shrink-0 transition-transform duration-150',
        open && 'rotate-180',
        active ? 'text-cime-gold-text' : open ? 'text-[#043429]' : 'text-[#8a928c]',
      )}
    >
      <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MenuMark({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'grid size-[18px] shrink-0 place-items-center rounded-[5px] border-[1.5px]',
        checked ? 'border-[#043429] bg-[#043429] text-white' : 'border-[#d4cfc3] bg-white',
      )}
    >
      {checked && (
        <svg viewBox="0 0 12 12" className="h-3 w-3">
          <path d="M2.2 6.2 4.8 8.8 9.8 3.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}
